import React, { FC, useState, useCallback, useEffect, useMemo } from 'react'
import isEqual from 'react-fast-compare'
import clsx from 'clsx'
import { useQueryParam, StringParam } from 'use-query-params'
import uniqBy from 'lodash.uniqby'
import { notification } from 'antd'
import { X, XOctagon, Calendar } from 'react-feather'

import {
  Divider as AntDivider,
  useShowMessage,
  useUtils,
  Tooltip,
  useAuthState,
  Button,
  Spin,
} from '@pepper/ui'

import { getUserId } from '@pepper/utils/src/api/functions/authHelpers'
import { updateCountry } from '@pepper/utils/src/api/companies/companies'
import {
  editCompanyDetails,
  editDetails,
  fetchUserDetails,
} from '@pepper/utils/src/api/users/users'
import { verifyOrder } from '@pepper/utils/src/api/projects/projects'
import getQuotation from '@pepper/utils/src/api/projects/getQuotation'
import {
  getReadableAmount,
  convertCurrencyToRupee,
  getCurrencyName,
} from '@pepper/utils/src/function/currency'
import getSelectedExperienceLevel from '@pepper/utils/src/function/getSelectedExperienceLevel'
import { getAssignmentPayload } from '@pepper/utils/src/function/verticalMap'
import {
  dataLayerTransactionSuccessEvent,
  dataLayerCheckoutEvent,
} from '@pepper/utils/src/function/dataLayerEvents'

import {
  useSelectedCompanyId,
  getUserCompanySetupComplete,
  getLanguageById,
} from 'utils/functions/misc'

import PaymentFailed from './PaymentStatus/PaymentFailed'
import PromoCodeModal from './PromoCode/PromoCode'
import CollectCountry from './CollectCountry/CollectCountry'
import BillingInformationForm from './BillingInfo/BillingInfo'

import EditPencil from 'assets/images/edit.svg'
import QueryIcon from 'assets/images/billingQuery.svg'

import styles from './_.module.css'

const getBillingInfo = (
  data?: UserDetails,
  companyId?: string
): SmeBillingInformation => {
  if (!data || !companyId) {
    return {
      company_name: '',
      registered_company_name: '',
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      country_code: 'IN',
      zip_code: '',
      company_gst: '',
    }
  }

  const company = data.companies.find(_company => _company.id === companyId)

  return {
    company_name: company?.company_name ?? '',
    registered_company_name: company?.registered_company_name ?? '',
    address_1: company?.address_1 ?? '',
    address_2: company?.address_2 ?? '',
    city: company?.city ?? '',
    state: company?.state ?? '',
    country_code: company?.country_code ?? 'IN',
    zip_code: company?.zip_code ?? '',
    company_gst: company?.company_gst ?? '',
  }
}

type FormProps = Record<string, unknown>
type Props = {
  data: any
  assignmentsList: AssignmentListFormValues[]
  paymentFormRef?: React.RefObject<any>
  updatePaymentCompletion?: (value: IPaymentStatus) => void
  companyCurrency?: string | null
  blockAllFormNavigation?: (val: boolean) => void
  blockBackFormNavigation?: (val: boolean) => void
  blockForwardFormNavigation?: (val: boolean) => void
  paymentExtraInfo?: any
  updateProjectFlow?: (
    values: FormProps,
    multiSelectValues?: IMultiSelectData | null,
    updateDraftOnDemand?: boolean,
    updatePaymentInfoAsSuccess?: boolean,
    stripeURL?: Window | null
  ) => void
}

const Payment: FC<Props> = ({
  data,
  paymentFormRef,
  updatePaymentCompletion,
  assignmentsList,
  companyCurrency,
  blockAllFormNavigation,
  blockForwardFormNavigation,
  blockBackFormNavigation,
  paymentExtraInfo,
  updateProjectFlow,
}) => {
  // TODO: investigate whether putting this in useMemo will help
  const {
    rootExperienceLevel,
    rootVertical,
    verticalBasedProcessData,
    createProjectJSONVersion,
    rootCategoryID,
    accountType,
    paymentMethod,
  } = paymentExtraInfo

  /* derived state */
  const companyId = useSelectedCompanyId()
  const [draftId] = useQueryParam('draft_id', StringParam)
  const userId = getUserId()
  const { user, getAuthUser } = useAuthState()
  const showMessage = useShowMessage()
  const [isLoading, setIsLoading] = useState(true)

  const [loadingCountry, setLoadingCountry] = useState<boolean>(false)
  const [api, contextHolder] = notification.useNotification()
  const [showPaymentFailure, setShowPaymentFailure] = useState<boolean>(false)
  const [tempCountry, setTempCountry] = useState<string>('')
  const [countryInputVisible, setCountryInputVisible] = useState<boolean>(
    () => {
      return !companyCurrency
    }
  )

  useEffect(() => {
    if (showPaymentFailure) {
      api.info({
        className: styles.toastAlert,
        duration: 5,
        message: (
          <span className={styles.toastText}>
            Payment Failed. We were unable to process your last payment.
          </span>
        ),
        icon: <XOctagon className={styles.toastCircle} />,
        closeIcon: <X className={styles.toastCircle} />,
        placement: 'topRight',
      })
    }
  }, [showPaymentFailure, api])

  useEffect(() => {
    blockForwardFormNavigation?.(true)
    if (countryInputVisible) {
      blockForwardFormNavigation?.(true)
    }
  }, [countryInputVisible, blockForwardFormNavigation])

  useEffect(() => {
    !countryInputVisible && !isLoading && blockForwardFormNavigation?.(false)
  }, [isLoading, countryInputVisible, blockForwardFormNavigation])

  useEffect(() => {
    if (rootCategoryID === 4) {
      setIsLoading(false)
    }
  }, [rootCategoryID])

  const [showPromoCodeModal, setShowPromoCodeModal] = useState<boolean>(false)

  const { languages, project_categories, genres, timezones } = useUtils()
  const industryMapped = useMemo(() => {
    return genres?.find(genre => genre?.id === data?.genre_id)?.title ?? ''
  }, [data?.genre_id, genres])

  /* state */
  // const [isOrderOngoing, setIsOrderOngoing] = useState<boolean>(false)
  const [editBilling, setEditBilling] = useState(false)
  const [billingInformation, setBillingInformation] =
    useState<SmeBillingInformation>()
  const [initializeRazorpay, setInitializeRazorpay] = useState<boolean>(false)
  const [selectedPromoCode, setSelectedPromoCode] = useState<string>('')

  const selectedProjectCategory = useMemo(() => {
    if (!rootCategoryID) return null
    return (
      project_categories.find(
        prjCategory => prjCategory.id === rootCategoryID
      ) ?? null
    )
  }, [rootCategoryID, project_categories])

  /** note: `isCompanySetupDone` should be computed if
   * later company is not added during user creation.
   */
  const [isCompanySetupDone, setCompanySetupDone] = useState<boolean>(false)
  // const [companySetupDetails, setCompanySetupDetails] =
  //   useState<SmeCompanySetup>(getCompanyDetails())

  const [priceEstimation, setPriceEstimation] = useState<
    SmeQuotation | undefined
  >()

  const currencyType = priceEstimation?.payable_amount?.currency
  const paymentCurrency = getCurrencyName(currencyType || 1)

  const checkPaymentPending = () => {
    return (
      !data?.template_information?.payment_status ||
      data?.template_information?.payment_status?.type !== 'payment.captured'
    )
  }

  const isPaymentPending = checkPaymentPending()

  useEffect(() => {
    setCompanySetupDone(getUserCompanySetupComplete(user, companyId))
  }, [companyId, user])

  useEffect(() => {
    if (
      data?.template_information?.payment_status?.type === 'payment.captured'
    ) {
      blockBackFormNavigation?.(true)
    }
  }, [data, blockBackFormNavigation])

  const removeDuplicates = useCallback((data: Timezone[]) => {
    return uniqBy(data, 'country_name').sort((a, b) =>
      a.country_name.localeCompare(b.country_name)
    )
  }, [])

  /** user methods */
  const getUserDetails = useCallback(async () => {
    const { status, data, error } = await fetchUserDetails({
      id: userId,
    })

    if (status === 'ok' && data !== null) {
      setBillingInformation(getBillingInfo(data, companyId))
      // setCompanySetupDetails(getCompanyDetails(data, companyId))
    } else {
      showMessage({ type: 'error', message: error })
    }
  }, [userId, companyId, showMessage])

  useEffect(() => {
    userId && getUserDetails()
  }, [getUserDetails, userId])

  /** razorpay methods */
  const loadRazorPayScript = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const razorpaySDK = document.getElementById('razorpay-sdk')

      if (razorpaySDK) {
        resolve(true)
      } else {
        const script = document.createElement('script')
        script.src = 'https://checkout.razorpay.com/v1/checkout.js'
        script.id = 'razorpay-sdk'
        script.onload = () => resolve(true)
        script.onerror = () => reject(false)
        document.body.appendChild(script)
      }
    })
  }

  const selectedLanguage = useMemo(() => {
    if (selectedProjectCategory?.id === 3) {
      if (!Array.isArray(data.translation_languages_target_id)) return '---'

      return data.translation_languages_target_id
        .map((languageId: number) => {
          return getLanguageById(languages, languageId)?.title ?? '--'
        })
        .join(', ')
    }

    if (typeof data.language_id !== 'number') return '--'

    return getLanguageById(languages, data.language_id)?.title ?? '--'
  }, [languages, data, selectedProjectCategory])

  const dispatchPaymentSuccessEvents = useCallback(
    (orderId: string, vendorOrderId: string) => {
      if (priceEstimation === undefined) return

      const contentType = rootVertical?.title
      const industry =
        data?.genre_id &&
        genres.find(genre => genre.id === data.genre_id)?.title

      // * * change payment currency when we are going to accept currencies other than INR
      window.dataLayer?.push({
        event: 'payment-success',
        paymentAmount: priceEstimation?.payable_amount.amount,
        paymentCurrency,
      })

      // ecommerce events
      window.dataLayer?.push({
        ecommerce: null,
      })
      window.dataLayer?.push({
        event: 'ecommerce-payment-success',
        ecommerce: {
          currencyCode: paymentCurrency,
          purchase: {
            actionField: {
              // unique order id
              id: orderId,
              affiliation: '',
              // total amount with tax
              revenue: convertCurrencyToRupee(priceEstimation.payable_amount),
              tax: convertCurrencyToRupee(priceEstimation.total_tax),
              coupon: priceEstimation.discount.promo_code ?? '',
            },
            products: [
              {
                id: orderId,
                name: contentType,
                quantity: assignmentsList.length,
                price: (
                  convertCurrencyToRupee({
                    amount:
                      (priceEstimation.payable_amount.amount ?? 0) -
                      (priceEstimation.total_tax.amount ?? 0),
                    currency: priceEstimation.payable_amount.currency,
                  }) / assignmentsList.length
                ).toFixed(2),
                category: selectedProjectCategory?.title,
                dimension1: industry,
              },
            ],
          },
        },
      })

      dataLayerTransactionSuccessEvent({
        projectName: data.project_title,
        orderCategory: selectedProjectCategory?.title ?? '',
        industry: industryMapped,
        creatorSkill: data.experience_level.toString(),
        contentLanguage: selectedLanguage,
        contentType: contentType ?? '',
        itemQuantity: assignmentsList.length,
        currencyCode: paymentCurrency,
        ecommerce: {
          purchase: {
            actionField: {
              id: orderId,
              revenue: convertCurrencyToRupee(priceEstimation.payable_amount),
              tax: convertCurrencyToRupee(priceEstimation.total_tax),
              affiliation: '',
              shipping: '',
            },
            products: [
              {
                variant: '',
                brand: 'peppercontent',
                id: orderId,
                name: rootVertical?.title,
                quantity: assignmentsList.length,
                price: (
                  convertCurrencyToRupee({
                    amount:
                      (priceEstimation.payable_amount.amount ?? 0) -
                      (priceEstimation.total_tax.amount ?? 0),
                    currency: priceEstimation.payable_amount.currency,
                  }) / assignmentsList.length
                ).toFixed(2),
                category: selectedProjectCategory?.title,
                coupon: priceEstimation.discount.promo_code ?? '',
              },
            ],
          },
        },
        vendorOrderId,
      })
    },
    [
      priceEstimation,
      genres,
      assignmentsList.length,
      data.project_title,
      data.experience_level,
      data.genre_id,
      selectedLanguage,
      selectedProjectCategory,
      paymentCurrency,
      rootVertical,
      industryMapped,
    ]
  )

  const initiatePaymentMethod = useCallback(async () => {
    // setIsOrderOngoing(true)
    blockAllFormNavigation?.(true)
    try {
      let stripeURL: Window | null = null
      if (paymentMethod === 'RAZORPAY') {
        await loadRazorPayScript()
      } else {
        stripeURL = window.open(
          `${window.location.origin}/${companyId}/stripe-payment-progress`,
          '_blank'
        )
      }
      const payload = getAssignmentPayload({
        additionalPayload: {
          confirm_order: 'true',
        },
        assignmentsList,
        companyId,
        createProjectJSONVersion,
        genre_id: data?.genre_id,
        translation_languages_target_id: data?.translation_languages_target_id,
        rootCategoryID,
        rootExperienceLevel,
        rootVertical,
        selectedPromoCode,
        verticalBasedProcessData,
        content_website_page: data?.content_website_page,
        project_draft_id: draftId as string,
      } as any) as any
      let orderID: string
      let vendorOrderID: string

      if (
        paymentExtraInfo?.razorPayPaymentInfo?.data?.payment?.status ===
          'processing' &&
        priceEstimation?.payable_amount?.amount ===
          Number(
            paymentExtraInfo?.razorPayPaymentInfo?.data?.payment?.scaledAmount
          )
      ) {
        orderID = paymentExtraInfo?.razorPayPaymentInfo?.data?.orderId
        vendorOrderID =
          paymentExtraInfo?.razorPayPaymentInfo?.data?.vendorOrderId
      } else {
        const order = await getQuotation({
          ...payload,
          project_draft_id: draftId,
          ...(paymentMethod === 'STRIPE' && {
            name: data?.project_title || ' ',
            country_code: 'US',
          }),
        })
        orderID = order?.data?.order_id || ''
        vendorOrderID = order?.data?.vendor_order_id || ''
        if (
          (order?.data as any)?.checkout_session_url &&
          stripeURL &&
          orderID
        ) {
          stripeURL.location.href = (order?.data as any)?.checkout_session_url
        }
        orderID &&
          updateProjectFlow?.(
            {
              order_id: orderID,
              payment_status: null,
            },
            null,
            true,
            false,
            stripeURL
          )
      }
      if (!orderID) {
        stripeURL?.close()
        return showMessage({
          type: 'error',
          message: 'Payment initialization failed',
        })
      }
      if (priceEstimation) {
        const date = new Date()
        date.setDate(date.getDate() + priceEstimation.project_delivery_estimate)
        dataLayerCheckoutEvent({
          orderCategory:
            project_categories.find(cat => cat.id === data.project_category)
              ?.title ?? '',
          industry: industryMapped,
          creatorSkill: data.experience_level.toString(),
          contentLanguage:
            languages.find(lang => lang.id === data.language_id)?.title ?? '',
          estimatedDelivery: date.toString(),
          itemQuantity: assignmentsList.length,
          registeredName: billingInformation?.company_name ?? '',
          zipCode: billingInformation?.zip_code ?? '',
          state: billingInformation?.state ?? '',
          country: billingInformation?.country_code ?? '',
          city: billingInformation?.city ?? '',
          ecommerce: {
            checkout: {
              actionField: {
                id: orderID || '',
                revenue: convertCurrencyToRupee(priceEstimation.payable_amount),
                tax: convertCurrencyToRupee(priceEstimation.total_tax),
                coupon: priceEstimation.discount.promo_code ?? '',
              },
            },
          },
        })
      }

      if (stripeURL) {
        !orderID && stripeURL?.close()
        return
      }

      const placedAt = new Date().toISOString()

      const options = {
        key: import.meta.env.REACT_APP_RAZORPAY_KEY,
        currency: paymentCurrency,
        amount: priceEstimation?.payable_amount.amount,
        order_id: vendorOrderID,
        name: 'Pepper Content',
        // todo: replace logo with currentStep
        image: 'https://assets.peppercontent.io/icons/PepperLogo.svg',
        handler: async (response: IRazorPaySuccess) => {
          try {
            const { status, error } = await verifyOrder(orderID, {
              vendorPaymentId: response.razorpay_payment_id,
              verificationSignature: response.razorpay_signature,
              draft_id: draftId,
            })

            if (status === 'ok') {
              // setIsOrderOngoing(false)
              updatePaymentCompletion?.({
                type: 'payment.captured',
                data: { ...response, orderId: orderID },
                error: null,
                placedAt,
                discount: priceEstimation?.discount,
                payable_amount: priceEstimation?.payable_amount,
              })
              blockBackFormNavigation?.(true)

              // dispatch e-commerce event
              dispatchPaymentSuccessEvents(orderID, vendorOrderID)
              /** Note: Update Draft here */
            } else {
              showMessage({ type: 'error', message: error })
            }
            blockAllFormNavigation?.(false)
          } catch (error) {
            blockAllFormNavigation?.(false)
            showMessage({ type: 'error', message: error.message })
          }
        },
      }

      if (typeof window !== 'undefined' && typeof globalThis !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const razorpay = new (window as any).Razorpay(options)

        razorpay.open()

        razorpay.on(
          'payment.failed',
          (response: { error: IRazorPayFailure }) => {
            setShowPaymentFailure(true)
            updatePaymentCompletion?.({
              type: 'payment.failed',
              data: null,
              error: response.error,
              placedAt,
            })
          }
        )
      }
    } catch (error) {
      showMessage({ type: 'error', message: error.message })
    }
    blockAllFormNavigation?.(false)
  }, [
    blockAllFormNavigation,
    assignmentsList,
    companyId,
    createProjectJSONVersion,
    data?.genre_id,
    data?.translation_languages_target_id,
    data?.experience_level,
    data?.project_category,
    data?.language_id,
    rootCategoryID,
    rootExperienceLevel,
    rootVertical,
    selectedPromoCode,
    verticalBasedProcessData,
    priceEstimation,
    paymentCurrency,
    project_categories,
    industryMapped,
    languages,
    billingInformation?.company_name,
    billingInformation?.zip_code,
    billingInformation?.state,
    billingInformation?.country_code,
    billingInformation?.city,
    draftId,
    updatePaymentCompletion,
    blockBackFormNavigation,
    dispatchPaymentSuccessEvents,
    showMessage,
    updateProjectFlow,
    paymentMethod,
    data?.project_title,
  ])

  /* order methods */

  /* fetch project estimations */
  const fetchProjectQuotation = useCallback(async () => {
    setIsLoading(true)
    const payload = getAssignmentPayload({
      assignmentsList,
      companyId,
      createProjectJSONVersion,
      genre_id: data?.genre_id,
      translation_languages_target_id: data?.translation_languages_target_id,
      rootCategoryID,
      rootExperienceLevel,
      rootVertical,
      selectedPromoCode,
      verticalBasedProcessData,
      content_website_page: data?.content_website_page,
    }) as any

    if (accountType === 'standard') {
      const { data: quoteData, status, error } = await getQuotation(payload)

      if (status === 'ok' && quoteData !== null) {
        setPriceEstimation(quoteData)
      } else {
        showMessage({ type: 'error', message: error })
        setSelectedPromoCode('')
      }
    }
    setIsLoading(false)
  }, [
    assignmentsList,
    companyId,
    createProjectJSONVersion,
    data?.genre_id,
    data?.translation_languages_target_id,
    rootCategoryID,
    rootExperienceLevel,
    rootVertical,
    selectedPromoCode,
    showMessage,
    verticalBasedProcessData,
  ])

  const selectedExperienceLevel = getSelectedExperienceLevel(
    selectedProjectCategory?.id,
    data.experience_level
  )

  const handlePaymentOrder = async (values: SmeBillingInformation) => {
    // setIsOrderOngoing(true)
    blockAllFormNavigation?.(true)
    setBillingInformation(values)

    if (!isCompanySetupDone) {
      const companyDetailsPromise = editCompanyDetails({
        id: companyId,
        body: {
          registered_company_name: values.registered_company_name,
          city: values.city,
          address_1: values.address_1,
          address_2: values.address_2,
          zip_code: values.zip_code,
          state: values.state,
          country_code: values.country_code,
          company_gst: values.company_gst,
        },
      })

      const editCompanySetupStatusPromise = editDetails({
        id: userId,
        body: {
          company_setup_done: true,
        },
      })

      const [companyDetailsResponse, companySetupStatusResponse] =
        await Promise.all([
          companyDetailsPromise,
          editCompanySetupStatusPromise,
        ])
      if (
        companySetupStatusResponse.status === 'ok' &&
        companyDetailsResponse.status === 'ok'
      ) {
        getUserDetails()
        initiatePaymentMethod()
      } else {
        showMessage({
          type: 'error',
          message: companySetupStatusResponse.description,
        })
      }
    } else {
      if (!isEqual(values, billingInformation)) {
        const { description, status } = await editCompanyDetails({
          id: companyId,
          body: {
            registered_company_name: values.registered_company_name,
            address_1: values.address_1,
            zip_code: values.zip_code,
            state: values.state,
            country_code: values.country_code,
            company_gst: values.company_gst,
          },
        })

        if (status === 'ok') {
          initiatePaymentMethod()
          getUserDetails()
        } else {
          showMessage({
            type: 'error',
            message: description,
          })
        }
      } else {
        initiatePaymentMethod()
        getUserDetails()
      }
      blockAllFormNavigation?.(false)
    }
  }
  // create project quote
  useEffect(() => {
    if (data?.project_category === 4) {
      setIsLoading(false)
      return
    }
    if (
      companyId &&
      accountType === 'standard' &&
      data?.experience_level &&
      verticalBasedProcessData
    ) {
      fetchProjectQuotation()
    } else if (
      companyId &&
      verticalBasedProcessData &&
      accountType === 'enterprise'
    ) {
      setIsLoading(false)
    }
  }, [
    accountType,
    fetchProjectQuotation,
    companyId,
    selectedPromoCode,
    verticalBasedProcessData,
    data?.project_category,
    data?.experience_level,
  ])

  // displays razorpay modal after checking if the user has completed company setup process
  useEffect(() => {
    if (companyId && initializeRazorpay && data.experience_level) {
      setInitializeRazorpay(false)
      initiatePaymentMethod()
    }
  }, [
    initializeRazorpay,
    initiatePaymentMethod,
    companyId,
    data.experience_level,
  ])

  useEffect(() => {
    if (
      !billingInformation?.registered_company_name &&
      billingInformation?.country_code
    ) {
      setEditBilling(true)
    }
  }, [billingInformation])

  const updateCompanyCountry = useCallback(
    async (companyCountry: string) => {
      let updatedCountry
      if (companyCountry && companyCountry !== '') {
        updatedCountry = companyCountry
      } else {
        const timeOffset = new Date().getTimezoneOffset()
        updatedCountry = timeOffset === -330 ? 'IN' : 'US'
      }
      await updateCountry(companyId, updatedCountry)
      if (userId) {
        await getAuthUser()
        await getUserDetails()
      }
    },
    [userId, companyId, getAuthUser, getUserDetails]
  )

  useEffect(() => {
    try {
      if (
        paymentFormRef?.current?.errors &&
        Object.keys(paymentFormRef?.current?.errors)?.length > 0
      ) {
        setEditBilling(true)
      }
    } catch (error) {
      console.log(error)
    }
  }, [paymentFormRef, paymentFormRef?.current?.errors])

  const updateCountryData = async (countryCode: string) => {
    setLoadingCountry(true)
    await updateCompanyCountry(countryCode)
    await fetchProjectQuotation()
    setCountryInputVisible(false)
    blockForwardFormNavigation?.(false)
    setLoadingCountry(false)
  }

  if (countryInputVisible) {
    return (
      <CollectCountry
        setTempCountry={setTempCountry}
        tempCountry={tempCountry}
        updateCountry={updateCountryData}
        timezones={removeDuplicates(timezones)}
        loadingCountry={loadingCountry}
      />
    )
  }
  let errorPayment
  if (
    isPaymentPending &&
    priceEstimation?.payable_amount &&
    data?.template_information?.payment_status?.error?.metadata.order_id &&
    showPaymentFailure
  ) {
    errorPayment = (
      <PaymentFailed
        projectCategory={selectedProjectCategory?.title ?? ''}
        vertical={rootVertical?.title}
        totalPrice={priceEstimation?.payable_amount}
        assignmentList={assignmentsList}
        orderId={
          data?.template_information?.payment_status.error?.metadata.order_id
        }
        placedAt={data?.template_information?.payment_status.placedAt}
        displayRazorPay={initiatePaymentMethod}
      />
    )
  }

  let discountedData = priceEstimation?.discount
  if (data?.template_information?.payment_status?.type === 'payment.captured') {
    discountedData = data?.template_information?.payment_status?.discount
  }
  const getTotalPrice = () => {
    if (
      data?.template_information?.payment_status?.type === 'payment.captured' &&
      data?.template_information?.payment_status?.payable_amount
    ) {
      return data?.template_information?.payment_status?.payable_amount
    }
    return priceEstimation?.payable_amount || { amount: 0, currency: 1 }
  }

  return (
    <Spin spinning={isLoading}>
      <div className={styles.wrapper}>
        <PromoCodeModal
          addPromoCodeModal={showPromoCodeModal}
          onCancel={() => setShowPromoCodeModal(false)}
          promoCodesList={priceEstimation?.available_promo_codes?.data || []}
          handleApplyPromoSubmit={initiatePaymentMethod}
          setSelectedPromoCode={setSelectedPromoCode}
          selectedPromoCode={selectedPromoCode}
          isLoading={isLoading}
          companyId={companyId}
          accountType={accountType as ClientAccountType}
          // projectStructure={data}
          // assignmentsList={assignmentsList}
          // projectBrief={data}
          // brandGuidelines={data}
        />
        <div className={styles.headingContainer}>
          <div className={styles.payHeader}>Order Summary</div>
        </div>
        {/* Billing Section */}
        <Divider />

        {accountType === 'standard' ? (
          <div className={styles.billingThreeColumn}>
            <p>Item</p>
            <p>Quantity</p>
            <p>Item Total</p>
          </div>
        ) : (
          <div className={styles.billingTwoColumn}>
            <p>Item</p>
            <p>Quantity</p>
          </div>
        )}
        <AntDivider className={clsx(styles.dividerTop, styles.divider)} />
        <div>
          {accountType === 'standard' ? (
            <div className={styles.billingThreeColumn}>
              <p>
                <div className={styles.billingItemHeading}>
                  {rootVertical?.title}
                </div>
                <p className={styles.billingItemDetails}>
                  {selectedProjectCategory?.id === 1 && (
                    <>
                      {selectedLanguage}
                      <br />
                    </>
                  )}
                  {selectedExperienceLevel}
                </p>
              </p>
              <p>{assignmentsList?.length}</p>
              <p className={styles.subBillingAmount}>
                {priceEstimation?.sub_total &&
                  getReadableAmount(priceEstimation?.sub_total)}
              </p>
            </div>
          ) : (
            <div className={styles.billingTwoColumn}>
              <p>
                <div className={styles.billingItemHeading}>
                  {rootVertical?.title}
                </div>
                <p className={styles.billingItemDetails}>
                  {selectedProjectCategory?.id === 1 && (
                    <>
                      {selectedLanguage}
                      <br />
                    </>
                  )}
                  {selectedExperienceLevel}
                </p>
              </p>
              <p className={styles.assignmentQuantity}>
                {assignmentsList?.length}
              </p>
            </div>
          )}
        </div>

        {accountType !== 'enterprise' ? (
          <>
            <Divider />
            {currencyType === 1 && (
              <>
                <div className={styles.billingThreeColumn}>
                  <p>
                    <div className={styles.billingItemSubHeading}>
                      Taxes & Charges
                    </div>
                    <p className={styles.billingItemDetails}>
                      18% GST is applicable
                    </p>
                  </p>
                  <p />
                  <p className={styles.subBillingAmount}>
                    {' '}
                    {priceEstimation?.total_tax &&
                      getReadableAmount(priceEstimation?.total_tax)}
                  </p>
                </div>
                <Divider />
              </>
            )}

            {discountedData?.promo_code && (
              <>
                <div className={styles.billingThreeColumn}>
                  <p>
                    <div className={styles.billingItemSubHeading}>
                      Discount Applied
                    </div>
                  </p>
                  <p />
                  <p className={styles.subBillingAmount}>
                    <div>
                      {' - '}
                      {getReadableAmount({
                        amount: discountedData?.promo_code_discount_amount ?? 0,
                        currency: discountedData?.currency,
                      })}
                    </div>
                    <div className={styles.promoCodeSection}>
                      <span className={styles.appliedPromoCodeLabel}>
                        {' '}
                        Applied Code:
                      </span>
                      <span className={styles.promoCodeValue}>
                        {' '}
                        {discountedData?.promo_code ?? '-'}
                      </span>
                    </div>
                    {isPaymentPending && (
                      <Button
                        type="link"
                        onClick={() => setSelectedPromoCode('')}
                        className={styles.addPromo}
                      >
                        Remove Promo Code
                      </Button>
                    )}
                  </p>
                </div>
                <Divider type="secondary" />
              </>
            )}

            <div className={styles.billingThreeColumn}>
              <p>
                <div className={styles.billingItemSubHeading}>
                  Total Payable Amount
                </div>
                {priceEstimation && (
                  <p className={styles.billingItemDetails}>
                    Estimated delivery in{' '}
                    <span className={styles.deliveryEstimate}>
                      {`${priceEstimation?.project_delivery_estimate}-${
                        priceEstimation &&
                        priceEstimation?.project_delivery_estimate + 2
                      } days`}
                    </span>
                    .
                    <Tooltip
                      placement="right"
                      title={
                        <span className={styles.tooltipText}>
                          This figure is only an estimate, not the actual date
                          of delivery
                        </span>
                      }
                    >
                      <img
                        src={QueryIcon}
                        alt="query"
                        className={styles.queryIcon}
                      />
                    </Tooltip>
                  </p>
                )}
              </p>
              <p></p>
              <p>
                <h2 className={styles.billingTotalAmount}>
                  {priceEstimation?.payable_amount &&
                    getReadableAmount(getTotalPrice())}
                </h2>
                {!selectedPromoCode && !isLoading && isPaymentPending && (
                  <Button
                    type="link"
                    onClick={() => setShowPromoCodeModal(true)}
                    className={styles.addPromo}
                  >
                    + Add Promo Code
                  </Button>
                )}
              </p>
            </div>
            {!editBilling && billingInformation && (
              <div
                role="none"
                onClick={() => {
                  isPaymentPending && setEditBilling(true)
                }}
                className={styles.billingCollapsed}
              >
                Billing address is{' '}
                <span className={styles.companyName}>
                  {`${billingInformation?.registered_company_name}, ${billingInformation?.city}`}
                </span>
                {'  '}
                {isPaymentPending && (
                  <img
                    className={styles.editAddress}
                    src={EditPencil}
                    alt="edit"
                  />
                )}
              </div>
            )}
            {contextHolder}
            {billingInformation && (
              <section
                className={clsx(styles.billingWrap, {
                  [styles.editBilling]:
                    editBilling &&
                    (!data?.template_information?.payment_status ||
                      data?.template_information?.payment_status?.type !==
                        'payment.captured'),
                })}
              >
                <BillingInformationForm
                  paymentFormRef={paymentFormRef}
                  billingInformation={billingInformation}
                  loading={isLoading}
                  totalPrice={getTotalPrice()}
                  onSubmit={handlePaymentOrder}
                />
                {/* End of Billing Section */}
              </section>
            )}
            {errorPayment}
          </>
        ) : null}
        {accountType === 'enterprise' && (
          <>
            <Divider type="primary" />
            <div className={styles.billingfooter}>
              <div className={styles.billingfooterIcon}>
                <Calendar />
              </div>
              <span>
                We will process this order and notify you on the estimated
                completion date within the next working 24 Hours
              </span>
            </div>
          </>
        )}
      </div>
    </Spin>
  )
}

export default Payment

function Divider({ type = 'primary' }) {
  if (type === 'secondary') {
    return <AntDivider className={styles.dividerTwo} />
  }
  return <AntDivider className={styles.divider} />
}
